import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ArrowLeft, Heart, Eye, Sparkles, Target, Users, Brain, Zap, Check, ChevronRight, BookOpen, MessageCircle, Lightbulb } from 'lucide-react';

// "Say What You Mean" - Mindful Approach to NVC based on Oren Jay Sofer's work
// Summary of Principles from the book integrated with NVC practice

export default function SayWhatYouMean() {
  const navigate = useNavigate();
  const [expandedSection, setExpandedSection] = useState('foundations');
  const [completedPrinciples, setCompletedPrinciples] = useState(() => {
    const saved = localStorage.getItem('sayWhatYouMeanProgress');
    return saved ? JSON.parse(saved) : [];
  });

  const togglePrinciple = (id) => {
    setCompletedPrinciples(prev => {
      const updated = prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id];
      localStorage.setItem('sayWhatYouMeanProgress', JSON.stringify(updated));
      return updated;
    });
  };

  // The Three Steps to Effective Conversation
  const threeSteps = [
    {
      id: 'step1',
      number: 1,
      title: 'Lead with Presence',
      color: 'from-blue-500 to-cyan-500',
      icon: Eye,
      description: 'Be in the moment, sink into calm. Undisturbed by emotions, grounded in awareness.',
      details: [
        'Effective communication requires presence',
        'Transformation occurs through small shifts sustained over time',
        'Presence lays the ground for connection',
        'Begin conversation with awareness, return to and maintain that awareness',
        'Be honest with yourself about what\'s happening',
        'The more aware we are, the more choice we have'
      ]
    },
    {
      id: 'step2',
      number: 2,
      title: 'Come from Curiosity and Care',
      color: 'from-rose-500 to-pink-500',
      icon: Heart,
      description: 'Approach with tender concern for the other person—not for what they do or say, but for the root feelings coursing through them.',
      details: [
        'Authentic curiosity is key—this echoes our approach',
        'Hold the most tender concern for the other person',
        'Focus not on their actions (which may be harmful) but on their root feelings',
        'With utmost tenderness and sincerity, discover the unmet need',
        'The core elemental requirement of existence that is not being met',
        'When we are tender, they become tender to us'
      ]
    },
    {
      id: 'step3',
      number: 3,
      title: 'Focus on What Matters',
      color: 'from-emerald-500 to-green-500',
      icon: Target,
      description: 'There is always a piece of gold on the table. An outcome that contributes to all parties always exists when we practice.',
      details: [
        'There\'s always a piece of gold on the table',
        'An outcome that contributes to all parties always exists',
        'This is created when we all do this practice',
        'It just takes practice',
        'Differentiate between strategies and needs',
        'By establishing tenderness, we find the goal and solutions'
      ]
    }
  ];

  // The Three Foundations
  const threeFoundations = [
    {
      id: 'presence',
      title: 'Presence',
      subtitle: 'The First Foundation',
      color: 'bg-blue-500/20 border-blue-500/50',
      textColor: 'text-blue-300',
      principles: [
        'Effective communication requires presence',
        'Given the complexity of communication, transformation occurs most readily through small shifts sustained over time',
        'Presence lays the ground for connection',
        'Lead with presence; begin conversation with awareness, return to and strive to maintain that awareness',
        'Be honest with yourself about what\'s happening',
        'The more aware we are, the more choice we have',
        'Leading with presence includes mutuality, seeing the other person as an autonomous individual',
        'Embrace uncertainty, acknowledging and accepting the unknown—both create new possibilities in dialogue'
      ]
    },
    {
      id: 'intention',
      title: 'Intention',
      subtitle: 'The Second Foundation',
      color: 'bg-amber-500/20 border-amber-500/50',
      textColor: 'text-amber-300',
      principles: [
        'Intention determines direction',
        'Our intentions, views, and experiences reinforce each other',
        'Views determine intentions, intentions shape experiences, experiences confirm our views',
        'Shifting our view therefore can change our intentions and our experience',
        'Being aware of our habitual conflict styles allows us to transform',
        'Conflict styles to notice: avoidance, competitive confrontation, passivity, passive-aggressive',
        'The less blame and criticism, the easier it is for others to hear us',
        'Everything we do, we do to meet a need'
      ]
    },
    {
      id: 'attention',
      title: 'Attention',
      subtitle: 'The Third Foundation',
      color: 'bg-emerald-500/20 border-emerald-500/50',
      textColor: 'text-emerald-300',
      principles: [
        'Attention shapes experience',
        'The more we differentiate between our strategies and needs, the more clarity and choice we have',
        'The more we understand one another, the easier it is to find solutions that work for everyone',
        'Establish as much mutual understanding as possible before problem solving',
        'Being aware of our emotions supports our ability to choose consciously how we participate',
        'The more we take responsibility for our feelings, the easier it is for others to hear us',
        'The more we hear others\' feelings as a reflection of their needs, the easier it is to understand without blame',
        'Having empathy for ourselves increases our capacity to listen to others'
      ]
    }
  ];

  // Additional Wisdom Principles
  const wisdomPrinciples = [
    {
      id: 'wisdom1',
      text: 'People are more likely to listen when they feel heard. To build understanding, reflect before you respond.',
      icon: MessageCircle
    },
    {
      id: 'wisdom2',
      text: 'Stating how a conversation can contribute to both of us helps create buy-in and willingness.',
      icon: Users
    },
    {
      id: 'wisdom3',
      text: 'Check if the other person feels understood before moving on to a new topic.',
      icon: Check
    },
    {
      id: 'wisdom4',
      text: 'We have more clarity and power when we use fewer words with more sincerity. Speak in short, succinct chunks.',
      icon: Zap
    },
    {
      id: 'wisdom5',
      text: 'Attending to our own reactivity, noticing the rise of activation and supporting the calm of deactivation, helps us make wiser choices.',
      icon: Brain
    },
    {
      id: 'wisdom6',
      text: 'When in conflict, if we aim to listen to the other person first, it increases the chances that they will be willing to listen to us.',
      icon: Heart
    },
    {
      id: 'wisdom7',
      text: 'State clearly what happened, without judgment or evaluation. This makes it easier for someone to hear us and work toward a solution.',
      icon: Eye
    },
    {
      id: 'wisdom8',
      text: 'Have ideas for strategies that meet as many needs as possible, which invites others to look for creative solutions.',
      icon: Lightbulb
    }
  ];

  // When People Are Under Duress
  const duressGuidance = {
    title: 'When People Are Under Duress',
    description: 'When people are overwhelmed with emotion, they are not in a place where they will listen. No amount of telling them what to do will work.',
    solution: 'You need to impress with genuine curiosity and care, tenderness. Then they will be tender to you and we can get things done.',
    steps: [
      'Recognize they cannot hear you right now',
      'Lead with presence and calm',
      'Show genuine curiosity about their experience',
      'Offer tender concern for their feelings',
      'Wait for receptivity before problem-solving'
    ]
  };

  const progress = Math.round((completedPrinciples.length / (threeFoundations.reduce((a, f) => a + f.principles.length, 0) + wisdomPrinciples.length)) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Decorative Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-emerald-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/3 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 sticky top-0">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="ghost"
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div className="h-6 w-px bg-slate-700"></div>
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-amber-400" />
                <span className="text-slate-300 text-sm">Deep Dive Resource</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-sm">{progress}% explored</span>
              <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-400 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-6 py-12 max-w-5xl">
        
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <span className="text-amber-300 text-sm font-medium">Mindful Communication Practice</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Say What You Mean
          </h1>
          <p className="text-xl text-slate-300 mb-2">A Mindful Approach to Nonviolent Communication</p>
          <p className="text-slate-400 text-sm">Based on the teachings of Oren Jay Sofer</p>
        </div>

        {/* Core Philosophy Quote */}
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-800/40 border border-slate-700/50 rounded-2xl p-8 mb-12">
          <blockquote className="text-center">
            <p className="text-xl md:text-2xl text-white leading-relaxed italic mb-4">
              "Shifting our motivation shifts reality. When we can all do this practice, there is always a piece of gold on the table—an outcome that contributes to all parties."
            </p>
            <footer className="text-amber-400">— Core Principle</footer>
          </blockquote>
        </div>

        {/* The Three Steps Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
              <span className="text-white font-bold">3</span>
            </div>
            <h2 className="text-2xl font-bold text-white">Steps to Effective Conversation</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {threeSteps.map((step) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.id}
                  className="group relative"
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.color} rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity`}></div>
                  <Card className="relative h-full bg-slate-800/50 border-slate-700/50 hover:border-slate-600/50 transition-colors">
                    <CardHeader>
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center mb-4`}>
                        <Icon className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-3xl font-bold bg-gradient-to-br ${step.color} bg-clip-text text-transparent`}>
                          {step.number}
                        </span>
                        <span className="text-slate-500 text-sm">Step</span>
                      </div>
                      <CardTitle className="text-white text-xl">{step.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-300 mb-4">{step.description}</p>
                      <ul className="space-y-2">
                        {step.details.slice(0, 3).map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                            <Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                            {detail}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </section>

        {/* The Three Foundations */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">The Three Foundations</h2>
          </div>

          <div className="space-y-6">
            {threeFoundations.map((foundation) => (
              <div 
                key={foundation.id}
                className={`rounded-2xl border ${foundation.color} overflow-hidden transition-all`}
              >
                <button
                  onClick={() => setExpandedSection(expandedSection === foundation.id ? '' : foundation.id)}
                  className="w-full px-6 py-5 flex items-center justify-between hover:bg-slate-800/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-3 h-3 rounded-full ${foundation.color.replace('/20', '/60').replace('bg-', 'bg-')}`}></div>
                    <div className="text-left">
                      <h3 className={`text-xl font-bold ${foundation.textColor}`}>{foundation.title}</h3>
                      <p className="text-slate-400 text-sm">{foundation.subtitle}</p>
                    </div>
                  </div>
                  <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${expandedSection === foundation.id ? 'rotate-90' : ''}`} />
                </button>
                
                {expandedSection === foundation.id && (
                  <div className="px-6 pb-6 space-y-3">
                    {foundation.principles.map((principle, idx) => {
                      const principleId = `${foundation.id}-${idx}`;
                      const isCompleted = completedPrinciples.includes(principleId);
                      return (
                        <button
                          key={idx}
                          onClick={() => togglePrinciple(principleId)}
                          className={`w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left ${
                            isCompleted ? 'bg-emerald-500/10' : 'bg-slate-800/30 hover:bg-slate-800/50'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                            isCompleted ? 'border-emerald-400 bg-emerald-400' : 'border-slate-500'
                          }`}>
                            {isCompleted && <Check className="h-3 w-3 text-white" />}
                          </div>
                          <span className={`text-sm ${isCompleted ? 'text-emerald-200' : 'text-slate-300'}`}>
                            {principle}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* When People Are Under Duress */}
        <section className="mb-16">
          <Card className="bg-gradient-to-br from-rose-900/30 to-slate-800/50 border-rose-700/30">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-rose-400" />
                </div>
                <div>
                  <CardTitle className="text-rose-300">{duressGuidance.title}</CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-300">{duressGuidance.description}</p>
              <div className="bg-slate-900/40 rounded-lg p-4 border border-rose-700/20">
                <p className="text-rose-200 italic">{duressGuidance.solution}</p>
              </div>
              <div className="grid md:grid-cols-5 gap-3 mt-4">
                {duressGuidance.steps.map((step, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <div className="w-6 h-6 rounded-full bg-rose-500/30 flex items-center justify-center mx-auto mb-2">
                      <span className="text-rose-300 text-xs font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-slate-300 text-xs">{step}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Wisdom Principles Grid */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Lightbulb className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">Wisdom Principles</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {wisdomPrinciples.map((principle) => {
              const Icon = principle.icon;
              const isCompleted = completedPrinciples.includes(principle.id);
              return (
                <button
                  key={principle.id}
                  onClick={() => togglePrinciple(principle.id)}
                  className={`flex items-start gap-4 p-4 rounded-xl border transition-all text-left ${
                    isCompleted 
                      ? 'bg-emerald-500/10 border-emerald-500/30' 
                      : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600/50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isCompleted ? 'bg-emerald-500/30' : 'bg-slate-700/50'
                  }`}>
                    <Icon className={`h-5 w-5 ${isCompleted ? 'text-emerald-400' : 'text-slate-400'}`} />
                  </div>
                  <p className={`text-sm ${isCompleted ? 'text-emerald-200' : 'text-slate-300'}`}>
                    {principle.text}
                  </p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Attribution */}
        <div className="text-center text-slate-500 text-sm border-t border-slate-700/50 pt-8">
          <p>Principles inspired by <em>"Say What You Mean: A Mindful Approach to Nonviolent Communication"</em></p>
          <p className="mt-1">by Oren Jay Sofer</p>
        </div>
      </div>
    </div>
  );
}
